import React, { useCallback, useEffect, useState } from 'react';

const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 14, border: '1px solid #ccc', borderRadius: 6, boxSizing: 'border-box' };
const textareaStyle = { ...inputStyle, minHeight: 72, resize: 'vertical' };
const btnStyle = { padding: '8px 14px', fontSize: 14, border: '1px solid #ccc', borderRadius: 6, background: '#fff', cursor: 'pointer' };
const btnPrimary = { ...btnStyle, background: '#7b4f2f', color: '#fff', borderColor: '#7b4f2f' };

const CHECK_ITEMS = [
  { key: 'basic', label: '活動基本資料已確認' },
  { key: 'product', label: '報名商品設定正確' },
  { key: 'notify', label: '通知設定檔已綁定' },
];

function rowVal(r, ...keys) {
  for (const k of keys) {
    if (r[k] != null && r[k] !== '') return r[k];
  }
  return '';
}

function buildEditFromProductDetail(d) {
  return {
    name: rowVal(d, 'NAME', 'name'),
    venue: rowVal(d, 'VENUE', 'venue', 'EVENT_LOCATION', 'eventLocation'),
    event_price: rowVal(d, 'EVENT_PRICE', 'eventPrice'),
    event_capacity: rowVal(d, 'EVENT_CAPACITY', 'eventCapacity'),
    member_price: rowVal(d, 'MEMBER_PRICE', 'memberPrice'),
    slug: rowVal(d, 'PRODUCT_SLUG', 'productSlug', 'SLUG', 'slug'),
    product_description: rowVal(d, 'PRODUCT_DESCRIPTION', 'productDescription', 'DESCRIPTION'),
    product_notes: rowVal(d, 'PRODUCT_NOTES', 'productNotes', 'NOTES'),
    payment_method: (() => {
      const raw = rowVal(d, 'PRODUCT_PAYMENT_METHODS', 'productPaymentMethods', 'PAYMENT_METHODS');
      if (!raw) return 'cash';
      try {
        const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
        return Array.isArray(arr) && arr[0] ? arr[0] : 'cash';
      } catch {
        return 'cash';
      }
    })(),
  };
}

export default function OrganizerProductReviewDrawer({
  open,
  productId,
  onClose,
  onDone,
  adminAuthHeaders,
}) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [edit, setEdit] = useState({});
  const [checks, setChecks] = useState({});
  const [approvalNote, setApprovalNote] = useState('');
  const [saving, setSaving] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/vb/admin/organizer-pending-product/${productId}`, { headers: adminAuthHeaders() });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.error || '載入失敗');
      const d = data.data || {};
      setDetail(d);
      setEdit(buildEditFromProductDetail(d));
      setChecks({});
      setApprovalNote('');
    } catch (e) {
      window.alert(e.message || '載入失敗');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [productId, adminAuthHeaders, onClose]);

  useEffect(() => {
    if (open && productId) loadDetail();
    if (!open) {
      setDetail(null);
      setEdit({});
      setChecks({});
    }
  }, [open, productId, loadDetail]);

  const npReady = !!(detail?.EFFECTIVE_NOTIFICATION_PROFILE_ID ?? detail?.effectiveNotificationProfileId);
  const npName = rowVal(detail || {}, 'EFFECTIVE_NOTIFICATION_PROFILE_NAME', 'effectiveNotificationProfileName')
    || rowVal(detail || {}, 'PRODUCT_NOTIFICATION_PROFILE_NAME', 'productNotificationProfileName')
    || rowVal(detail || {}, 'ORG_DEFAULT_NOTIFICATION_PROFILE_NAME', 'orgDefaultNotificationProfileName');
  const allChecked = CHECK_ITEMS.every((c) => checks[c.key]);

  const putBody = () => ({
    name: edit.name,
    venue: edit.venue,
    eventPrice: edit.event_price,
    eventCapacity: edit.event_capacity,
    memberPrice: edit.member_price,
    slug: edit.slug,
    paymentMethod: edit.payment_method,
    productDescription: edit.product_description,
    productNotes: edit.product_notes,
  });

  const saveFields = async () => {
    setSaving(true);
    try {
      const res = await fetch(`/api/vb/admin/organizer-pending-product/${productId}`, {
        method: 'PUT',
        headers: adminAuthHeaders(),
        body: JSON.stringify(putBody()),
      });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.error || '儲存失敗');
      window.alert('已儲存');
      await loadDetail();
    } catch (e) {
      window.alert(e.message || '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    if (!allChecked) return window.alert('請勾選所有檢查項目');
    if (!npReady) return window.alert('尚未設定通知設定檔，請至「主辦帳號」設定全系統預設或個別通知檔');
    setSaving(true);
    try {
      await fetch(`/api/vb/admin/organizer-pending-product/${productId}`, {
        method: 'PUT',
        headers: adminAuthHeaders(),
        body: JSON.stringify(putBody()),
      });
      const res = await fetch(`/api/vb/admin/organizer-pending-product/${productId}/approve`, {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ approval_note: approvalNote }),
      });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.error || '核准失敗');
      window.alert('已核准上架');
      onDone();
      onClose();
    } catch (e) {
      window.alert(e.message || '核准失敗');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const base = typeof window !== 'undefined' ? window.location.origin : '';
  const regUrl = edit.slug ? `${base}/venue/booking/p/${edit.slug}/form` : null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1100,
      display: 'flex', justifyContent: 'flex-end',
    }}
    >
      <div style={{
        width: 'min(720px, 100vw)', height: '100%', background: '#fff', overflow: 'auto',
        padding: 20, boxShadow: '-4px 0 24px rgba(0,0,0,.15)',
      }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>審核商品送審（無月曆）</h2>
          <button type="button" style={btnStyle} onClick={onClose}>關閉</button>
        </div>

        {loading ? (
          <p>載入中…</p>
        ) : detail ? (
          <>
            <p style={{ fontSize: 14, color: '#555', marginTop: 0 }}>
              主辦：{rowVal(detail, 'ORGANIZER_DISPLAY_NAME', 'organizerDisplayName')} ({rowVal(detail, 'ORGANIZER_LOGIN', 'organizerLogin')})
            </p>
            <p style={{ fontSize: 13, color: '#888' }}>
              商品 #{productId}
              {rowVal(detail, 'ACTIVITY_DATE', 'activityDate') ? ` · ${rowVal(detail, 'ACTIVITY_DATE', 'activityDate')}` : ''}
              {rowVal(detail, 'ACTIVITY_TIME', 'activityTime') ? ` ${rowVal(detail, 'ACTIVITY_TIME', 'activityTime')}` : ''}
            </p>

            <section style={{ marginBottom: 20, padding: 14, border: '1px solid #e8e8e8', borderRadius: 8 }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>報名商品（可編輯）</h3>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>活動名稱</label>
              <input style={{ ...inputStyle, marginBottom: 10 }} value={edit.name || ''} onChange={(e) => setEdit((p) => ({ ...p, name: e.target.value }))} />
              <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>地點</label>
              <input style={{ ...inputStyle, marginBottom: 10 }} value={edit.venue || ''} onChange={(e) => setEdit((p) => ({ ...p, venue: e.target.value }))} />
              <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>slug</label>
              <input style={{ ...inputStyle, marginBottom: 10 }} value={edit.slug || ''} onChange={(e) => setEdit((p) => ({ ...p, slug: e.target.value }))} />
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>報名費</label>
                  <input type="number" style={inputStyle} value={edit.event_price || ''} onChange={(e) => setEdit((p) => ({ ...p, event_price: e.target.value }))} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>會員價</label>
                  <input type="number" style={inputStyle} value={edit.member_price || ''} onChange={(e) => setEdit((p) => ({ ...p, member_price: e.target.value }))} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>名額</label>
                  <input type="number" style={inputStyle} value={edit.event_capacity || ''} onChange={(e) => setEdit((p) => ({ ...p, event_capacity: e.target.value }))} />
                </div>
              </div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>付款方式</label>
              <select style={{ ...inputStyle, marginBottom: 10 }} value={edit.payment_method || 'cash'} onChange={(e) => setEdit((p) => ({ ...p, payment_method: e.target.value }))}>
                <option value="cash">現金（現場付款）</option>
                <option value="ecpay_credit">信用卡</option>
              </select>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>說明</label>
              <textarea style={{ ...textareaStyle, marginBottom: 10 }} rows={4} value={edit.product_description || ''} onChange={(e) => setEdit((p) => ({ ...p, product_description: e.target.value }))} />
              {regUrl && (
                <p style={{ fontSize: 13, color: '#7b4f2f', wordBreak: 'break-all' }}>
                  報名連結：<a href={regUrl} target="_blank" rel="noopener noreferrer">{regUrl}</a>
                </p>
              )}
            </section>

            <section style={{ marginBottom: 20, padding: 14, border: '1px solid #e8e8e8', borderRadius: 8 }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>通知設定</h3>
              <p style={{ fontSize: 13, margin: 0, color: npReady ? '#3d7a3d' : '#c0392b' }}>
                {npReady ? `已就緒：${npName || '（已綁定）'}` : '尚未綁定通知設定檔'}
              </p>
            </section>

            <section style={{ marginBottom: 20, padding: 14, border: '1px solid #e8e8e8', borderRadius: 8 }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>核准前檢查</h3>
              {CHECK_ITEMS.map((c) => (
                <label key={c.key} style={{ display: 'block', marginBottom: 8, fontSize: 14 }}>
                  <input type="checkbox" checked={!!checks[c.key]} onChange={(e) => setChecks((p) => ({ ...p, [c.key]: e.target.checked }))} />
                  {' '}{c.label}
                </label>
              ))}
              <label style={{ display: 'block', marginTop: 12, marginBottom: 4, fontSize: 13 }}>核准備註（選填）</label>
              <textarea style={textareaStyle} rows={2} value={approvalNote} onChange={(e) => setApprovalNote(e.target.value)} />
            </section>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" style={btnStyle} onClick={saveFields} disabled={saving}>儲存修改</button>
              <button type="button" style={btnPrimary} onClick={approve} disabled={saving || !allChecked}>核准上架</button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
